import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Gift, RotateCw, BadgeCheck, MousePointerClick, Star } from "lucide-react";
import { toast } from "sonner";
import client, { formatApiErrorDetail } from "@/lib/api";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { useTexts } from "@/lib/texts";

const REVIEW_URL = "https://g.page/r/CQwXksy7eV0PEBM/review";

// ticchettio durante il giro + campanellino alla vincita (Web Audio, nessun file)
function playTick() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = 1800;
    gain.gain.setValueAtTime(0.05, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.05);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.05);
  } catch { /* audio non disponibile */ }
}

function playWinChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1108.73, 1318.51].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const t = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.12, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.6);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.6);
    });
  } catch { /* audio non disponibile */ }
}

const SEGMENTS = [
  "sconto5", "sconto10eur", "gadget", "sconto5", "plus1euro", "sconto10", "sconto5", "lucidatura",
  "sconto10", "sconto5", "sconto20eur", "gadget", "sconto5", "plus1euro", "sconto10eur", "sconto50eur",
];

const PRIZE_SHORT = {
  sconto10: "SCONTO 10%",
  gadget: "GADGET OMAGGIO",
  sconto5: "SCONTO 5%",
  plus1euro: "+1€ AL GRAMMO",
  lucidatura: "PULIZIA GIOIELLO",
  sconto10eur: "SCONTO 10€",
  sconto20eur: "SCONTO 20€",
  sconto50eur: "SCONTO 50€",
};

const PRIZES_LIST = [
  "Sconto del 10% su tutti i gioielli della vetrina",
  "Gadget omaggio da ritirare in negozio",
  "Sconto del 5% su tutti i gioielli della vetrina",
  "+1 € al grammo (al netto della caratura) sulla valutazione in negozio",
  "Pulizia gioiello omaggio con lavaggio ad ultrasuoni (porta il tuo gioiello in oro)",
  "Sconto di 10 € su un gioiello del catalogo (min. 99 €)",
  "Sconto di 20 € su un gioiello del catalogo (min. 189 €)",
  "Sconto di 50 € su un gioiello del catalogo (min. 399 €)",
];

const SEG_ANGLE = 360 / SEGMENTS.length;
const R = 250;
const C = 265;

function polar(angleDeg, radius) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return [C + radius * Math.cos(a), C + radius * Math.sin(a)];
}

function segmentPath(i) {
  const a0 = i * SEG_ANGLE;
  const a1 = a0 + SEG_ANGLE;
  const [x0, y0] = polar(a0, R);
  const [x1, y1] = polar(a1, R);
  return `M ${C} ${C} L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
}

const inputCls =
  "mt-2 w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-[#FAFAFA] transition-colors placeholder:text-[#737373]";
const labelCls = "text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]";

export default function Promo() {
  const texts = useTexts();
  const [form, setForm] = useState({ nome: "", cognome: "", email: "", privacy: false });
  const [spinning, setSpinning] = useState(false);
  const [pending, setPending] = useState(null);
  const [result, setResult] = useState(null);
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const [error, setError] = useState("");
  const [hubAlert, setHubAlert] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [respinDone, setRespinDone] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: k === "privacy" ? e.target.checked : e.target.value }));

  const formValid =
    form.nome.trim() !== "" && form.cognome.trim() !== "" && form.email.trim() !== "" && form.privacy;

  const rotorRef = useRef(null);
  const rotationRef = useRef(0);

  const currentAngle = () => {
    const el = rotorRef.current;
    if (!el) return rotationRef.current;
    const t = getComputedStyle(el).transform;
    if (t && t !== "none") {
      const m = new DOMMatrix(t);
      return Math.atan2(m.b, m.a) * (180 / Math.PI);
    }
    return rotationRef.current;
  };

  const runSpin = (fromDeg, toDeg, onDone) => {
    const el = rotorRef.current;
    if (!el) {
      onDone();
      return;
    }
    const normFrom = ((fromDeg % 360) + 360) % 360;
    const anim = el.animate(
      [{ transform: `rotate(${normFrom}deg)` }, { transform: `rotate(${toDeg}deg)` }],
      { duration: 6000, easing: "cubic-bezier(0.15, 0.85, 0.15, 1)", fill: "forwards" }
    );
    rotationRef.current = toDeg;
    // ticchettio che segue il rallentamento della ruota
    const tickTimes = [];
    let t = 120;
    while (t < 5700) {
      tickTimes.push(t);
      t += 70 + (t / 5700) * 520;
    }
    tickTimes.forEach((ms) => setTimeout(playTick, ms));
    const finish = () => {
      rotationRef.current = toDeg;
      playWinChime();
      onDone();
    };
    anim.onfinish = finish;
    // fallback di sicurezza: se onfinish non scatta, chiudo comunque lo spin
    setTimeout(() => {
      if (anim.playState !== "finished") {
        try { anim.finish(); } catch { /* noop */ }
      }
      finish();
    }, 6800);
  };

  const doSpin = async () => {
    if (spinning) return;
    setError("");
    setAlreadyPlayed(false);
    try {
      const { data } = await client.post("/promo/spin", { ...form, review_bonus: respinDone });
      const idxs = SEGMENTS.map((s, i) => (s === data.prize.id ? i : -1)).filter((i) => i >= 0);
      const target = idxs[Math.floor(Math.random() * idxs.length)];
      const center = target * SEG_ANGLE + SEG_ANGLE / 2;
      const from = currentAngle();
      const mod = ((from % 360) + 360) % 360;
      const delta = (((360 - center) - mod) % 360 + 360) % 360;
      setPending(data);
      setSpinning(true);
      runSpin(from, mod + 360 * 6 + delta, () => {
        setSpinning(false);
        setResult(data);
        setPending(null);
        toast.success("Hai vinto! Controlla la tua email per il coupon.");
      });
    } catch (err) {
      if (err.response?.status === 409) {
        setAlreadyPlayed(true);
        setError(formatApiErrorDetail(err.response?.data?.detail));
      } else {
        const msg = formatApiErrorDetail(err.response?.data?.detail);
        setError(msg);
        toast.error(msg);
      }
    }
  };

  const submit = (e) => {
    e.preventDefault();
    doSpin();
  };

  const hubClick = () => {
    if (spinning || result) return;
    if (!formValid) {
      setHubAlert(true);
      setTimeout(() => setHubAlert(false), 3500);
      return;
    }
    doSpin();
  };

  const retrySpin = () => {
    setRespinDone(true);
    setReviewed(false);
    setResult(null);
    setError("");
    setAlreadyPlayed(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="mx-auto max-w-7xl px-5 pb-28 pt-32 md:px-8 md:pt-40" data-testid="promo-page">
      <Reveal>
        <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Un premio ogni giorno</p>
      </Reveal>
      <h1 className="mt-4 font-serif text-5xl leading-[1.02] md:text-7xl">
        <MaskedLine delay={0.1}>Scopri la tua</MaskedLine>
        <MaskedLine delay={0.25}>
          <em className="text-gold-gradient">promo</em>.
        </MaskedLine>
      </h1>
      <Reveal delay={0.4}>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-[#A3A3A3] md:text-lg">
          {texts.promo_sub}
        </p>
      </Reveal>

      <div className="mt-16 grid items-center gap-14 lg:grid-cols-12">
        {/* WHEEL */}
        <Reveal className="lg:col-span-7">
          <div className="relative mx-auto max-w-[560px]" data-testid="wheel-container">
            <div className="gold-spotlight left-1/2 top-1/2 h-[120%] w-[120%] -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute left-1/2 top-[-6px] z-20 -translate-x-1/2" data-testid="wheel-pointer">
              <div className="h-0 w-0 border-x-[14px] border-t-[22px] border-x-transparent border-t-gold drop-shadow-[0_2px_6px_rgba(212,175,55,0.5)]" />
            </div>

            <AnimatePresence>
              {hubAlert && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.95 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-1/2 top-8 z-30 w-64 -translate-x-1/2 border border-gold/50 bg-[#0d0d0d]/95 px-5 py-4 text-center shadow-[0_20px_60px_-15px_rgba(212,175,55,0.35)] backdrop-blur-sm"
                  data-testid="wheel-hub-alert"
                >
                  <MousePointerClick size={16} className="mx-auto text-gold" />
                  <p className="mt-2 text-xs leading-relaxed text-gold-pale">
                    Inserisci nome, cognome ed email qui accanto per ottenere il tuo giro omaggio.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            <div
              ref={rotorRef}
              className="will-change-transform"
              style={{ animation: "spin-slow 40s linear infinite", transformOrigin: "center" }}
              data-testid="wheel-rotor"
            >
              <svg viewBox={`0 0 ${C * 2} ${C * 2}`} className="w-full" data-testid="wheel-svg">
                <circle cx={C} cy={C} r={R + 8} fill="none" stroke="#D4AF37" strokeWidth="3" />
                <circle cx={C} cy={C} r={R + 14} fill="none" stroke="rgba(212,175,55,0.25)" strokeWidth="1" />
                {SEGMENTS.map((seg, i) => {
                  const isGold = i % 2 === 0;
                  const mid = i * SEG_ANGLE + SEG_ANGLE / 2;
                  const [tx, ty] = polar(mid, 152);
                  const rot = mid > 90 && mid < 270 ? mid + 90 : mid - 90;
                  const [dx, dy] = polar(mid, 228);
                  return (
                    <g key={i}>
                      <path
                        d={segmentPath(i)}
                        fill={isGold ? "#D4AF37" : "#0d0d0d"}
                        stroke={isGold ? "#0d0d0d" : "rgba(212,175,55,0.35)"}
                        strokeWidth="1"
                      />
                      <text
                        x={tx}
                        y={ty}
                        fill={isGold ? "#050505" : seg === "sconto50eur" ? "#D4AF37" : "#E8DCC0"}
                        fontSize="12.5"
                        fontFamily="'Playfair Display', serif"
                        fontWeight="600"
                        letterSpacing="1.5"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        transform={`rotate(${rot} ${tx} ${ty})`}
                      >
                        {PRIZE_SHORT[seg]}
                      </text>
                      <circle cx={dx} cy={dy} r="3.5" fill={isGold ? "#050505" : "#D4AF37"} opacity="0.8" />
                    </g>
                  );
                })}
                <g
                  onClick={hubClick}
                  style={{ cursor: "pointer" }}
                  data-testid="wheel-hub-button"
                  role="button"
                  aria-label="Gira la ruota"
                >
                  <circle cx={C} cy={C} r="62" fill="#050505" stroke="#D4AF37" strokeWidth="2" />
                  <circle cx={C} cy={C} r="56" fill="none" stroke="rgba(212,175,55,0.25)" strokeWidth="1" />
                  <image
                    href="/assets/logo-gold.png"
                    x={C - 34}
                    y={C - 26}
                    width="68"
                    height="24"
                    preserveAspectRatio="xMidYMid meet"
                  />
                  <text
                    x={C}
                    y={C + 26}
                    textAnchor="middle"
                    fill="#D4AF37"
                    fontSize="14"
                    fontWeight="700"
                    fontFamily="Manrope, sans-serif"
                    letterSpacing="4"
                  >
                    GIRA
                  </text>
                </g>
              </svg>
            </div>
          </div>
        </Reveal>

        {/* FORM / RESULT */}
        <div className="lg:col-span-5">
          {result ? (
            <Reveal>
              <div className="border border-gold/40 bg-[#0d0d0d] p-8 md:p-10" data-testid="promo-result-panel">
                <BadgeCheck size={28} className="text-gold" />
                <p className="mt-4 text-[10px] uppercase tracking-[0.35em] text-gold">Hai vinto</p>
                <h2 className="mt-3 font-serif text-2xl leading-snug md:text-3xl" data-testid="promo-prize-label">
                  {result.prize.label}
                </h2>
                <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">Il tuo codice coupon</p>
                <p className="mt-2 bg-gold px-4 py-4 text-center font-mono text-2xl font-bold tracking-[0.2em] text-black" data-testid="promo-code-value">
                  {result.code}
                </p>
                <p className="mt-6 text-sm leading-relaxed text-[#A3A3A3]">
                  Ti abbiamo inviato il coupon via email. Fai uno screenshot e presentalo in negozio: verificheremo il codice e ritirerai il premio.
                </p>

                {!respinDone ? (
                  <div className="mt-8 border-t border-white/10 pt-7">
                    {!reviewed ? (
                      <>
                        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Vuoi rigirare subito?</p>
                        <p className="mt-3 text-sm leading-relaxed text-[#A3A3A3]">
                          Lasciaci una recensione su Google facendoci sapere come ci hai conosciuto o come ti sei trovato con noi: in cambio ti regaliamo un secondo giro oggi stesso.
                        </p>
                        <a
                          href={REVIEW_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setReviewed(true)}
                          data-testid="promo-review-link"
                          className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-gold px-5 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover"
                        >
                          <Star size={14} className="fill-current" />
                          Lascia una recensione
                        </a>
                      </>
                    ) : (
                      <>
                        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Grazie di cuore</p>
                        <p className="mt-3 text-sm leading-relaxed text-[#A3A3A3]">
                          La tua recensione vale oro: ecco il tuo secondo giro, buona fortuna!
                        </p>
                        <button
                          type="button"
                          onClick={retrySpin}
                          data-testid="promo-respin-button"
                          className="mt-5 flex w-full items-center justify-center gap-3 bg-gold py-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-black transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover"
                        >
                          <RotateCw size={15} />
                          Rigira la ruota
                        </button>
                        <p className="mt-4 text-xs leading-relaxed text-[#737373]">
                          Se si è chiusa la pagina della recensione senza salvarla,{" "}
                          <a href={REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="promo-review-reopen" className="text-gold underline underline-offset-2">
                            aprila di nuovo da qui
                          </a>
                          .
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="mt-8 border-t border-white/10 pt-7 text-sm leading-relaxed text-[#A3A3A3]" data-testid="promo-respin-used">
                    Hai già usato il tuo giro extra di oggi. Il turno si rinnova a mezzanotte: torna a trovarci domani!
                  </p>
                )}
              </div>
            </Reveal>
          ) : (
            <Reveal delay={0.2}>
              <form onSubmit={submit} className="border border-white/10 bg-[#0d0d0d] p-8 md:p-10" data-testid="promo-form">
                <div className="flex items-center gap-3">
                  <Gift size={20} className="text-gold" />
                  <h2 className="font-serif text-2xl">Gira e vinci</h2>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#A3A3A3]">
                  {texts.promo_sub}
                </p>
                <div className="mt-7 grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="promo-nome" className={labelCls}>Nome *</label>
                    <input id="promo-nome" data-testid="promo-nome-input" required value={form.nome} onChange={set("nome")} className={inputCls} placeholder="Mario" />
                  </div>
                  <div>
                    <label htmlFor="promo-cognome" className={labelCls}>Cognome *</label>
                    <input id="promo-cognome" data-testid="promo-cognome-input" required value={form.cognome} onChange={set("cognome")} className={inputCls} placeholder="Rossi" />
                  </div>
                </div>
                <div className="mt-5">
                  <label htmlFor="promo-email" className={labelCls}>Email *</label>
                  <input id="promo-email" data-testid="promo-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="nome@email.it" />
                </div>
                <label className="mt-6 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-[#A3A3A3]">
                  <input
                    type="checkbox"
                    required
                    data-testid="promo-privacy-checkbox"
                    checked={form.privacy}
                    onChange={set("privacy")}
                    className="mt-0.5 accent-[#D4AF37]"
                  />
                  Autorizzo il trattamento dei miei dati personali ai sensi del GDPR, per la partecipazione alla promozione e l'invio del coupon via email. *
                </label>

                {error && (
                  <p
                    className={`mt-5 border px-4 py-3 text-sm ${alreadyPlayed ? "border-gold/40 bg-gold/10 text-gold-pale" : "border-red-900/50 bg-red-950/30 text-red-300"}`}
                    data-testid={alreadyPlayed ? "promo-already-played" : "promo-error"}
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={spinning}
                  data-testid="promo-spin-button"
                  className="mt-7 flex w-full items-center justify-center gap-3 bg-gold py-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-black transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover disabled:opacity-60"
                >
                  <RotateCw size={15} className={spinning ? "animate-spin" : ""} />
                  {spinning ? "La ruota gira…" : "Gira la ruota"}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </div>

      {/* PREMI IN PALIO */}
      <section className="mt-28" data-testid="promo-prizes-section">
        <Reveal>
          <p className="text-[10px] uppercase tracking-[0.4em] text-gold">In palio oggi</p>
        </Reveal>
        <MaskedLine className="mt-4 font-serif text-3xl md:text-5xl" delay={0.1}>I premi della ruota</MaskedLine>
        <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {PRIZES_LIST.map((p, i) => (
            <Reveal key={p} delay={(i % 4) * 0.08} className="bg-[#050505]">
              <div className="flex h-full flex-col p-7" data-testid={`promo-prize-${i}`}>
                <span className="outline-number font-serif text-4xl">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-4 text-sm leading-relaxed text-[#D4D4D4]">{p}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 text-xs leading-relaxed text-[#737373]">
            Un giro al giorno per indirizzo email, più un secondo tentativo lasciando una recensione su Google; il turno si rinnova a mezzanotte. I coupon sono personali, non cumulabili e presentabili in negozio entro 30 giorni dalla vincita. *Il bonus +1 €/g si applica al netto della caratura sulla valutazione in negozio.
          </p>
        </Reveal>
      </section>
    </main>
  );
}
